import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, Ship, Package, FileText, AlertTriangle, Receipt,
  Award, BarChart3, Bot, ChevronRight, Search, Bell, ListChecks,
  FileSignature, FolderCheck, Settings as SettingsIcon, Zap, ShieldCheck, LogOut,
  CalendarDays, Network, Plug,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/dashboard", label: "Control Tower", icon: LayoutDashboard, group: "Overview" },
  { to: "/procurement", label: "Procurement & Rates", icon: BarChart3, group: "Before Shipment" },
  { to: "/pricing", label: "WeDelvr Pricing", icon: Zap, group: "Before Shipment" },
  { to: "/schedules", label: "Sailing Schedules", icon: CalendarDays, group: "Before Shipment" },
  { to: "/rfq", label: "RFQ / Tender", icon: FileSignature, group: "Before Shipment" },
  { to: "/bookings", label: "Bookings", icon: ListChecks, group: "Before Shipment" },
  { to: "/shipments", label: "Shipment Visibility", icon: Ship, group: "During Shipment" },
  { to: "/documents", label: "Document Compliance", icon: FolderCheck, group: "During Shipment" },
  { to: "/detention", label: "Detention & Demurrage", icon: AlertTriangle, group: "During Shipment" },
  { to: "/invoices", label: "Invoice Audit", icon: Receipt, group: "After Shipment" },
  { to: "/carriers", label: "Carrier Scorecard", icon: Award, group: "After Shipment" },
  { to: "/network", label: "Stakeholder Network", icon: Network, group: "Collaboration" },
  { to: "/consultant", label: "AI Consultant", icon: Bot, group: "Intelligence" },
  { to: "/settings", label: "Integrations", icon: Plug, group: "Account" },
  { to: "/admin", label: "Admin Console", icon: ShieldCheck, group: "Account", adminOnly: true },
];

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const groups = NAV.reduce((acc, item) => {
    if (item.adminOnly && !isAdmin) return acc;
    acc[item.group] = acc[item.group] || [];
    acc[item.group].push(item);
    return acc;
  }, {});
  const current = NAV.find(n => location.pathname.startsWith(n.to));

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="app-shell">
      {/* Top bar */}
      <header className="h-14 border-b border-zinc-200 bg-white flex items-center px-6 sticky top-0 z-30">
        <div className="flex items-center gap-3 w-64">
          <div className="w-7 h-7 bg-[#002FA7] flex items-center justify-center">
            <span className="text-white font-mono text-[11px] font-bold tracking-tighter">CT</span>
          </div>
          <div className="leading-tight">
            <div className="font-heading font-black text-[15px] tracking-tight">WEDELVR</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Control Tower</div>
          </div>
        </div>
        <div className="flex-1 flex items-center gap-2 max-w-md ml-6">
          <Search className="w-4 h-4 text-zinc-400" />
          <Input
            data-testid="global-search"
            placeholder="Search shipment, container, PO, invoice..."
            className="h-9 border-zinc-200 rounded-none focus-visible:ring-[#002FA7]"
          />
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Button
            data-testid="topbar-alerts-btn"
            variant="ghost"
            size="icon"
            className="rounded-none relative"
            onClick={() => navigate("/dashboard")}
          >
            <Bell className="w-4 h-4" />
            <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-[#DC2626] rounded-full animate-pulse-dot" />
          </Button>
          <div className="flex items-center gap-2 pl-3 border-l border-zinc-200">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 hover:bg-zinc-50 px-2 py-1 -my-1 transition-colors">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                  ) : (
                    <div className="w-7 h-7 bg-zinc-900 text-white flex items-center justify-center text-[11px] font-mono">{(user?.name || user?.email || "?")[0]?.toUpperCase()}</div>
                  )}
                  <div className="leading-tight text-left">
                    <div className="text-xs font-medium">{user?.name || user?.email || "Guest"}</div>
                    <div className="text-[10px] text-zinc-500">{user?.role === "ADMIN" ? "Admin" : user?.customer_group || "User"}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none">
                <DropdownMenuLabel className="text-xs text-zinc-500">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isAdmin && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="rounded-none cursor-pointer">
                    <ShieldCheck className="w-3.5 h-3.5 mr-2" /> Admin Console
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="rounded-none cursor-pointer text-[#991B1B]">
                  <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 border-r border-zinc-200 bg-white min-h-[calc(100vh-3.5rem)] sticky top-14">
          <nav className="py-3">
            {Object.entries(groups).map(([group, items]) => (
              <div key={group} className="mb-4">
                <div className="px-6 mb-1 text-[10px] uppercase tracking-[0.25em] text-zinc-400">{group}</div>
                {items.map(item => {
                  const Icon = item.icon;
                  const active = location.pathname.startsWith(item.to);
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      data-testid={`nav-${item.to.replace('/', '')}`}
                      data-active={active}
                      className="nav-link flex items-center gap-3 px-6 py-2.5 text-sm text-zinc-700"
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            ))}
          </nav>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0">
          {current && (
            <div className="px-8 pt-6 pb-3 flex items-center text-xs text-zinc-500 gap-2">
              <span className="uppercase tracking-[0.2em]">WeDelvr</span>
              <ChevronRight className="w-3 h-3" />
              <span className="uppercase tracking-[0.2em] text-zinc-900">{current.label}</span>
            </div>
          )}
          <div className="px-8 pb-12">{children}</div>
        </main>
      </div>
    </div>
  );
}
